import { useMemo } from 'react';

import CropSquareOutlined from '@mui/icons-material/CropSquareOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { Box, Typography } from '@/shared/ui';
import type { Theme } from '@/shared/ui';

import { spacing } from '@/config/ui';
import { Permission } from '@/entities/permissions';
import ShareButton from '@/features/_shared/ShareButton';
import UpgradeToPremiumButton from '@/features/_shared/UpgradeToPremium/UpgradeToPremiumButton';
import { usePermission, useUserDateFormat } from '@/features/_shared/user';
import { FACTSHEETS_ENDPOINT } from '@/features/factsheet/constants';
import {
  formatFactsheetDate,
  getVersionHistoryField,
  getFactsheetLink
} from '@/features/factsheet/lib';
import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';
import type { FactsheetDetails } from '@/features/factsheet/types';
import { StorageKeys } from '@/services/storage';
import { useStorageValue } from '@/services/storage/react';
import downloadPDF from '@/shared/lib/download/downloadPDF';
import Button from '@/shared/ui/primitives/Button';
import type { ButtonSize } from '@/shared/ui/primitives/Button';

import useItemsTab from '../FactsheetBody/Tabs/ItemsTab/useItemsTab';

const btnRowSx = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 1.5,
  flexShrink: 0
};
const rootSx = {
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  px: 3,
  width: '100%'
};
const compactRootSx = { px: 2, pt: 2 };
const topRowSx = { display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap' };
const titleBoxSx = { flexGrow: 1, mr: spacing['6'], maxWidth: 740, minWidth: 0 };
const shareBtnSx = { width: '120px' };
// UpgradeToPremiumButton hardcodes minWidth/fontSize in its own base sx, so
// the `compact` Button size preset alone can't shrink it here — override both.
const compactUpgradeBtnSx = (theme: Theme) => ({
  minWidth: 'auto',
  height: theme.typography.pxToRem(28),
  fontSize: theme.typography.pxToRem(12)
});

interface ButtonSectionProps {
  factsheet: FactsheetDetails;
}

const ButtonSection = ({ factsheet }: ButtonSectionProps) => {
  const isCompact = useIsFactsheetCompact();
  const { handleAllItemsSelection, allItemsSelected } = useItemsTab(factsheet);

  const handleDownload = (): void => {
    downloadPDF(`${FACTSHEETS_ENDPOINT}/${factsheet.code}.pdf`, `${factsheet.code}.pdf`);
  };

  const { hasPermission } = usePermission();
  const isPermissionGranted = hasPermission(Permission.ViewIndexInformationChart);
  const btnSize: ButtonSize = isCompact ? 'compact' : 'medium';

  return (
    <Box sx={{ ...btnRowSx, py: isCompact ? 1 : spacing['2'] }}>
      {!isPermissionGranted && (
        <UpgradeToPremiumButton
          sx={isCompact ? compactUpgradeBtnSx : undefined}
          type='upgrade'
        />
      )}
      <ShareButton
        iconSize={12}
        shareUrl={getFactsheetLink(factsheet.code)}
        size={btnSize}
        sx={shareBtnSx}
        title={factsheet.title}
      />
      <Button
        color='secondary'
        disabled={allItemsSelected}
        size={btnSize}
        startIcon={<CropSquareOutlined />}
        variant='contained'
        onClick={handleAllItemsSelection}
      >
        Add item(s) to selector
      </Button>
      <Button
        color='secondary'
        size={btnSize}
        startIcon={<FileDownloadOutlined />}
        variant='contained'
        onClick={handleDownload}
      >
        Download factsheet
      </Button>
    </Box>
  );
};

interface DescriptionProps {
  description: string | undefined;
}

const Description = ({ description }: DescriptionProps) => {
  const isCompact = useIsFactsheetCompact();
  if (!description) return null;

  const lineCounts = new Map<string, number>();
  const lines = description.split('\n').map(line => {
    const occurrence = lineCounts.get(line) ?? 0;
    lineCounts.set(line, occurrence + 1);
    return {
      key: `${line || 'empty-line'}-${occurrence}`,
      text: line
    };
  });

  return (
    <Box>
      {lines.map(line => (
        <Typography
          key={line.key}
          sx={theme => ({
            mb: 0.5,
            fontSize: theme.typography.pxToRem(isCompact ? 14 : 20)
          })}
        >
          {line.text}
        </Typography>
      ))}
    </Box>
  );
};

interface VersionInfoProps {
  version: string;
  validFrom: string;
  validTo: string;
}

const VersionInfo = ({ version, validFrom, validTo }: VersionInfoProps) => {
  const isCompact = useIsFactsheetCompact();

  return (
    <Typography
      sx={theme => ({
        fontSize: isCompact ? theme.typography.pxToRem(12) : undefined,
        fontStyle: 'italic',
        pt: '2px'
      })}
      variant='body1'
    >
      Factsheet version {version}
      {validFrom && `, valid from ${validFrom}`}
      {validTo && ` to ${validTo}`}
    </Typography>
  );
};

interface FactsheetHeaderProps {
  factsheet: FactsheetDetails;
}

const FactsheetHeader = ({ factsheet }: FactsheetHeaderProps) => {
  const gxToken = useStorageValue(StorageKeys.GxToken);
  const dateFormat = useUserDateFormat();
  const isCompact = useIsFactsheetCompact();

  const validFrom = useMemo(
    () =>
      formatFactsheetDate(
        getVersionHistoryField(factsheet, 'factsheetValidFrom'),
        dateFormat.value
      ),
    [dateFormat.value, factsheet]
  );

  const validTo = useMemo(
    () =>
      formatFactsheetDate(getVersionHistoryField(factsheet, 'factsheetValidTo'), dateFormat.value),
    [dateFormat.value, factsheet]
  );

  return (
    <Box sx={isCompact ? [rootSx, compactRootSx] : rootSx}>
      <Box sx={topRowSx}>
        <Box sx={titleBoxSx}>
          <Typography
            size={isCompact ? 16 : 24}
            sx={{ fontWeight: 700 }}
          >
            {factsheet.title}
          </Typography>
        </Box>
        {gxToken && <ButtonSection factsheet={factsheet} />}
      </Box>
      <Description description={factsheet.description} />
      <VersionInfo
        validFrom={validFrom}
        validTo={validTo}
        version={factsheet.latestFactsheetVersion}
      />
    </Box>
  );
};

export default FactsheetHeader;
